/**
 * Flex pricing for self-serve with card on file (D1 and D2, ruled 2026-10-08; prices from Eula's billing spec).
 *
 * Flex has no contract, no down payment and no hiring volume: the customer saves a card, and each hire is charged when
 * an offer is made, at the price of that role's salary band. So nothing here is a quote to pay: it is what each hire
 * will cost. Above $500K is priced individually (D4) and has no number here.
 *
 * KEEP IN SYNC BY HAND with the Stripe catalog's flex_* prices (scripts/stripe/catalog.json in avrio-backend). The
 * server never takes a price from this file: the charge at offer is computed from the hire's own salary.
 */

export type SalaryBand = 'under100k' | 'from100kTo200k' | 'from200kTo500k';

/** A band a self-serve customer can be priced in, or "above", which is quoted by hand. */
export type SalaryBandChoice = SalaryBand | 'above500k';

/** Dollars per hire on Flex, by salary band. */
export const FLEX_PRICE_PER_HIRE: Readonly<Record<SalaryBand, number>> = {
  under100k: 1200,
  from100kTo200k: 3600,
  from200kTo500k: 12000,
};

/** How each band is named on screen. The question and option wording is copy (see copy.ts, S1). */
export const SALARY_BAND_LABEL: Readonly<Record<SalaryBand, string>> = {
  under100k: 'Under $100K',
  from100kTo200k: '$100K–$200K',
  from200kTo500k: '$200K–$500K',
};

export interface FlexQuote {
  band: SalaryBand;
  pricePerHire: number;
}

/** null for above $500K: there is no self-serve price to show, only a conversation. */
export const flexQuoteFor = (choice: SalaryBandChoice): FlexQuote | null =>
  choice === 'above500k'
    ? null
    : { band: choice, pricePerHire: FLEX_PRICE_PER_HIRE[choice] };
